import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import GererPersonnelModal from '@/components/feature/GererPersonnelModal';
import BulkImportStaffModal from '@/components/feature/BulkImportStaffModal';

export default function SageFemmesPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(false);
  const [sageFemmes, setSageFemmes] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    motDePasse: '',
    sexe: '',
  });
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Gérer une sage-femme
  const [selectedSF, setSelectedSF] = useState<any>(null);
  const [showGererModal, setShowGererModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }
    loadSageFemmes();
  }, [user, role, authLoading, navigate]);

  const loadSageFemmes = async () => {
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from('sage_femmes')
      .select('*')
      .order('created_at', { ascending: false });
    if (loadError) { console.error('Erreur chargement sage-femmes:', loadError); setLoading(false); return; }
    if (data) setSageFemmes(data);
    setLoading(false);
  };

  const createSageFemme = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingCreate(true);
    setError('');
    setSuccess('');
    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) throw new Error('Session expiree');
      const response = await supabase.functions.invoke('create-sage-femme', {
        body: {
          nom: formData.nom, prenom: formData.prenom, email: formData.email,
          telephone: formData.telephone || null, motDePasse: formData.motDePasse,
          sexe: formData.sexe || null,
        },
        headers: { Authorization: `Bearer ${authData.session.access_token}` },
      });
      if (response.error) throw response.error;
      setSuccess('Sage-femme creee avec succes !');
      setFormData({ nom: '', prenom: '', email: '', telephone: '', motDePasse: '', sexe: '' });
      setShowCreateForm(false);
      loadSageFemmes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la creation du compte');
      setTimeout(() => setError(''), 5000);
    } finally { setLoadingCreate(false); }
  };

  const toggleSageFemmeStatus = async (sfId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      const { error: updateError } = await supabase
        .from('sage_femmes').update({ bloque: newStatus }).eq('id', sfId);
      if (updateError) throw updateError;
      setSageFemmes(prev => prev.map(s => s.id === sfId ? { ...s, bloque: newStatus } : s));
      setSuccess(newStatus ? 'Sage-femme bloquee' : 'Sage-femme debloquee');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) { setError('Erreur lors de la modification du statut'); setTimeout(() => setError(''), 3000); }
  };

  const deleteSageFemme = async (sfId: string, userId: string) => {
    if (!confirm('Etes-vous sur de vouloir supprimer cette sage-femme ? Cette action est irreversible.')) return;
    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) throw new Error('Session expiree');
      const response = await supabase.functions.invoke('delete-sage-femme', {
        body: { sageFemmeId: sfId, userId },
        headers: { Authorization: `Bearer ${authData.session.access_token}` },
      });
      if (response.error) throw response.error;
      setSuccess('Sage-femme supprimee avec succes');
      await loadSageFemmes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const msg = err?.context?.error || err?.message || 'Erreur lors de la suppression';
      setError(msg); setTimeout(() => setError(''), 5000);
    }
  };

  const filteredSageFemmes = sageFemmes.filter(sf => {
    const s = searchQuery.toLowerCase();
    return sf.nom.toLowerCase().includes(s) || sf.prenom.toLowerCase().includes(s) ||
      sf.email.toLowerCase().includes(s) || (sf.telephone && sf.telephone.toLowerCase().includes(s));
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
          <div className="text-gray-600 text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Gestion des Sages-Femmes</h1>
                <p className="text-gray-600 mt-2">Creez et gerez les comptes des sages-femmes de la clinique</p>
              </div>
              <button onClick={() => navigate('/admin')} className="px-4 py-2 text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                ← Retour au tableau de bord
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i><span>{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-600 rounded-2xl flex items-start">
                <i className="ri-check-line text-xl mr-2 mt-0.5"></i><span>{success}</span>
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Liste des sages-femmes ({sageFemmes.length})</h2>
              <div className="flex gap-3">
                <button onClick={() => setShowImportModal(true)} className="px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-file-upload-line mr-2"></i>Importer (CSV)
                </button>
                <button onClick={() => setShowCreateForm(true)} className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-add-line mr-2"></i>Creer un compte sage-femme
                </button>
              </div>
            </div>

            {showCreateForm && (
              <div className="mb-8 p-6 bg-teal-50 border border-teal-200 rounded-2xl">
                <h3 className="text-lg font-semibold text-teal-800 mb-4">Nouveau compte sage-femme</h3>
                <form onSubmit={createSageFemme} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                      <input type="text" required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="w-full px-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prenom *</label>
                      <input type="text" required value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} className="w-full px-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sexe</label>
                    <select value={formData.sexe} onChange={(e) => setFormData({ ...formData, sexe: e.target.value })} className="w-full px-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm pr-8">
                      <option value="">Selectionner</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telephone</label>
                    <input type="tel" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} className="w-full px-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe *</label>
                    <input type="password" required minLength={10} value={formData.motDePasse} onChange={(e) => setFormData({ ...formData, motDePasse: e.target.value })} className="w-full px-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" placeholder="Minimum 10 caracteres" />
                  </div>
                  <div className="flex gap-4">
                    <button type="submit" disabled={loadingCreate} className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold disabled:opacity-50 whitespace-nowrap cursor-pointer">
                      {loadingCreate ? 'Creation...' : 'Creer le compte'}
                    </button>
                    <button type="button" onClick={() => setShowCreateForm(false)} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold whitespace-nowrap cursor-pointer">Annuler</button>
                  </div>
                </form>
              </div>
            )}

            <div className="mb-6">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                <input type="text" placeholder="Rechercher une sage-femme..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-teal-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" />
              </div>
            </div>

            <div className="space-y-4">
              {filteredSageFemmes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchQuery ? 'Aucune sage-femme trouvee' : 'Aucune sage-femme creee pour le moment'}
                </div>
              ) : (
                filteredSageFemmes.map((sf: any) => (
                  <div key={sf.id} className="border border-pink-100 rounded-2xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mr-4">
                          <i className="ri-nurse-line text-3xl text-pink-600"></i>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{sf.nom} {sf.prenom}</h3>
                          <p className="text-gray-600">{sf.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {sf.sexe && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">{sf.sexe}</span>
                            )}
                            {sf.telephone && (
                              <p className="text-sm text-gray-500"><i className="ri-phone-line mr-1"></i>{sf.telephone}</p>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            <i className="ri-calendar-line mr-1"></i>Cree le {new Date(sf.created_at).toLocaleDateString('fr-FR')}
                          </p>
                          {sf.bloque && (
                            <span className="inline-block mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Compte bloque</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setSelectedSF(sf); setShowGererModal(true); }} className="px-4 py-2 bg-teal-100 text-teal-700 rounded-full hover:bg-teal-200 font-medium transition-all whitespace-nowrap cursor-pointer">
                          <i className="ri-settings-3-line mr-1"></i>Gérer
                        </button>
                        <button onClick={() => toggleSageFemmeStatus(sf.id, sf.bloque)} className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${sf.bloque ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`}>
                          <i className={`${sf.bloque ? 'ri-lock-unlock-line' : 'ri-lock-line'} mr-1`}></i>
                          {sf.bloque ? 'Debloquer' : 'Bloquer'}
                        </button>
                        {isTrueSuperAdmin && (
                          <button onClick={() => deleteSageFemme(sf.id, sf.user_id)} className="px-4 py-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 font-medium transition-all whitespace-nowrap cursor-pointer">
                            <i className="ri-delete-bin-line mr-1"></i>Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <BulkImportStaffModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        staffType="sage_femme"
        onSuccess={loadSageFemmes}
      />

      <GererPersonnelModal
        isOpen={showGererModal}
        onClose={() => { setShowGererModal(false); setSelectedSF(null); }}
        type="sage_femme"
        personnel={selectedSF || { id: '', user_id: '', nom: '', prenom: '', email: '' }}
        onUpdate={loadSageFemmes}
      />

      <Footer />
    </div>
  );
}